/**
 * dimension-fhmsz — argument encoding for scheduled integrations.
 *
 * A schedule stores one integration as a single string, `Process/arg1/arg2`, and both job writers
 * (WebApps/Portal/Controllers/Setup/Scheduler.cs:992 "Run Now" and
 * AWS_Lambda/EventBridge_Scheduler/Function.cs:143) copy that string into `job.method_info`
 * verbatim. The Runner then splits on '/' and URL-decodes each segment EXACTLY ONCE
 * (dimensionx/Runner/Runner.cs:195).
 *
 * So the stored form must carry each argument encoded exactly once — the same contract the
 * run-integration box already satisfies via encodeURIComponent at
 * Pages/Integration/Components/IntegrationRow/IntegrationRow.tsx:243.
 *
 * The editor previously did no encoding in either direction, so a '/' typed into an argument
 * became a segment boundary and split into two arguments.
 */

/** Encodes one argument for storage in the joined integration string. */
export function encodeScheduleArg(value: string): string {
    return encodeURIComponent(value);
}

/**
 * Decodes one stored argument back to the text the user typed.
 *
 * Existing schedules pre-date the encoding and hold raw text, which can contain a bare '%' that is
 * not a valid escape ("50% complete") and would make decodeURIComponent throw. Those arguments are
 * already correct as-is, so fall back to the raw value rather than breaking the editor.
 */
export function decodeScheduleArg(part: string): string {
    try {
        return decodeURIComponent(part);
    } catch {
        return part;
    }
}

/** Splits a stored integration string into its process name and decoded arguments. */
export function splitScheduleIntegration(integration: string): {
    process: string;
    args: string[];
} {
    const [process, ...rest] = integration.split("/");
    return { process, args: rest.map(decodeScheduleArg) };
}

/**
 * Joins a process name and its raw (un-encoded) arguments back into the stored form, dropping
 * trailing empty arguments the way the editor always has.
 */
export function joinScheduleIntegration(process: string, args: string[]): string {
    // Array.from, not spread: assigning args[3] on a 1-element array leaves holes, and
    // encodeURIComponent(undefined) would store the text "undefined".
    const parts = Array.from(args, (a) => a ?? "");
    while (parts.length && parts[parts.length - 1] === "") parts.pop();
    return [process, ...parts.map(encodeScheduleArg)].join("/");
}

/** Renders a stored integration for display, with its arguments shown as the user typed them. */
export function formatScheduleIntegration(integration: string): string {
    const { process, args } = splitScheduleIntegration(integration);
    return [process, ...args].join("/");
}
